import type {
	AdminListPage,
	CatalogBrand,
	CatalogCategory,
	CatalogDashboard,
	CatalogProduct,
	CatalogProductImage,
	ProductHistoryPage,
	ProductItemKind,
	ProductPrice,
	ProductStatus,
	PublishBlocker,
	SpecTemplate,
	SpecTemplateAttribute,
} from "@shared/types";
import { catalogDashboardSchema, parseResponseData } from "@shared/types";
import { apiClient } from "@/lib/api-client";
import {
	adminBrandDetailSchema,
	adminBrandRowPageSchema,
	adminBrandSummaryPageSchema,
	adminCategoryRowListSchema,
	adminCategorySummaryListSchema,
	adminProductDetailSchema,
	adminProductPageSchema,
	brandCategoryCoverageListSchema,
	brandNearMatchListSchema,
	bulkUpdateResultSchema,
	categoryMoveAssessmentSchema,
	createdBrandSchema,
	createdCategorySchema,
	deletedTemplateSchema,
	mergePreviewSchema,
	mergeResultSchema,
	priceHistorySchema,
	productEnvelopeSchema,
	productHistorySchema,
	recordPriceResultSchema,
	reorderResultSchema,
	specTemplateImpactSchema,
	templateEnvelopeSchema,
	uploadedImageSchema,
	variantEnvelopeSchema,
} from "./catalog-api.schemas";

/**
 * The catalogue's admin API, and the ONE place product reads are composed.
 *
 * Every catalogue READ goes through here, deliberately -- products and brands
 * alike. Brand self-service is expected (the Feb plan's "Phase 2"), and a brand
 * user must see only their own products, so there needs to be exactly one place
 * that decides what a caller is allowed to list. `scopeFilters` below is that
 * place. It is a no-op today; when the brand-user story lands it gains a
 * `brandId` and every screen inherits the restriction without being touched.
 *
 * "Every read" is load-bearing and was briefly untrue: `listAdminBrands` went
 * through `toParams` but not the seam, so the one guarantee this paragraph
 * makes had an exception in the same file. A promise with an exception is worse
 * than no promise, because the next reader believes it.
 *
 * Building the scope now and the column later is the deliberate order. An
 * `admin_user_roles.brand_id` that nothing populates and nothing reads could
 * only be guessed at -- scoped to the grant, or to the person? -- and adding a
 * nullable column is the cheapest migration there is, before launch or after.
 * What is expensive later is retrofitting the seam through a dozen call sites,
 * and that is what this file prevents.
 *
 * Types come from `@shared/types`; nothing here redefines a catalogue shape.
 * `AdminBrandSummary` is a `Pick` of the shared `CatalogBrand` rather than a
 * hand-written twin for exactly that reason -- it used to be the twin, three
 * lines under this sentence.
 */

export type { AdminListPage };

export interface AdminProduct extends CatalogProduct {
	brandName: string | null;
}

export interface ProductFilters {
	status?: ProductStatus;
	verification?: string;
	source?: string;
	categoryId?: string;
	brandId?: string;
	q?: string;
	itemKind?: ProductItemKind;
	page?: number;
	perPage?: number;
}

/**
 * The scoping seam. Returns the filters a request may actually run with.
 *
 * EXPORTED because a caller that caches results has to key on what was actually
 * requested of the server, not on what it asked for. A cache key built from
 * pre-scope filters collides across two operators whose scopes differ, and the
 * second one is served the first one's rows -- which would turn this seam from a
 * restriction into a leak on the day it starts restricting anything.
 *
 * Unscoped today: an internal operator sees the whole catalogue, which is what
 * every current role is for. The signature is what matters -- when a brand-user
 * arrives, this forces their `brandId` in here and no screen has to know.
 *
 * NOT AN AUTHORIZATION BOUNDARY. This runs in the browser, so it shapes requests
 * and nothing more; a caller who wants a different filter can simply send one.
 * Enforcement is the server's -- `admin:catalog:read` and, when brand scoping
 * exists, a server-side clause. The value here is that every screen composes its
 * request in one place, so there is a single line to change rather than a dozen
 * call sites to find.
 */
export function scopeCatalogFilters<Filters extends object>(
	filters: Filters,
): Filters {
	// Generic rather than `ProductFilters`, so the brand list composes its request
	// through the same seam. Narrowing it to one screen's filter shape would have
	// meant a second, parallel scoping function -- and two places to remember when
	// scoping finally has to do something is exactly what this exists to avoid.
	return filters;
}

/**
 * The largest page the endpoint will serve.
 *
 * `adminListProductsSchema` and `adminListBrandsSchema` both cap at 100 and
 * answer 400 above it -- not a smaller page, an error. Naming it once means the
 * two callers cannot drift apart, and `listAdminProducts` clamps rather than
 * letting a caller compose a request the server is going to refuse.
 */
const MAX_PAGE_SIZE = 100;

/** Rows per page on the product list. Comfortably under `MAX_PAGE_SIZE`. */
export const PER_PAGE = 25;

/**
 * The sizes the product list offers (10, 25, 50, 100).
 *
 * 10 is for compact reviewing, 25 is standard `PER_PAGE`, 50 is a wide window,
 * and 100 is `MAX_PAGE_SIZE` -- the server's own cap, so the widest option matches
 * the server ceiling. It does NOT reach the 500-product bulk cap and is not meant to:
 * select-all-matching is what covers a filter, and page size only decides how
 * much of it an operator reads at a time.
 */
export const PAGE_SIZES = [10, PER_PAGE, 50, MAX_PAGE_SIZE] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

/**
 * Query params, with empty values dropped rather than sent blank -- so a filter
 * nobody set never reaches the wire as `?status=`.
 *
 * Constrained to the values it can actually serialise. An earlier version took a
 * bare `object` to admit callers other than `ProductFilters`, which also
 * admitted a nested object or an array -- values the `as string | number` below
 * would have cast a lie onto and axios would have serialised as
 * `[object Object]`. The generic keeps every caller welcome while still checking
 * what they pass; a plain `Record` parameter would not, because an `interface`
 * carries no index signature and `ProductFilters` is one.
 */
type ParamValue = string | number | boolean | undefined | null;

function toParams<T extends Partial<Record<keyof T, ParamValue>>>(
	filters: T,
): Record<string, string | number | boolean> {
	const params: Record<string, string | number | boolean> = {};
	for (const [key, value] of Object.entries(filters)) {
		if (value === undefined || value === null || value === "") continue;
		// `boolean` is in the type because the brand list filters on `isActive`.
		// It was already passing booleans through under a `string | number`
		// annotation, which axios happened to serialise correctly.
		params[key] = value as string | number | boolean;
	}
	return params;
}

export async function listAdminProducts(
	filters: ProductFilters,
): Promise<AdminListPage<AdminProduct>> {
	const response = await apiClient.get("/api/v1/admin/catalog/products", {
		params: toParams(scopeCatalogFilters(clampPageSize(filters))),
	});
	return parseResponseData(
		response.data,
		adminProductPageSchema,
		"GET /api/v1/admin/catalog/products",
	);
}

/** A `perPage` above the endpoint's cap is a 400, so it never leaves here. */
function clampPageSize(filters: ProductFilters): ProductFilters {
	if (filters.perPage === undefined) return filters;
	return { ...filters, perPage: Math.min(filters.perPage, MAX_PAGE_SIZE) };
}

/**
 * A brand as the filter dropdown needs it.
 *
 * A `Pick` of the shared shape, not a copy of three of its fields. The endpoint
 * actually returns the whole `CatalogBrand` plus a `productCount`; declaring a
 * lookalike here meant the client's idea of a brand could drift from the
 * server's without anything failing to compile -- which is precisely how the
 * `specifications` boolean mismatch happened, and why plan 008 V13 exists.
 */
export type AdminBrandSummary = Pick<
	CatalogBrand,
	"id" | "name" | "isActive" | "mergedIntoId"
>;

/**
 * Brands, for the list filter. `MAX_PAGE_SIZE` is the endpoint's own cap --
 * asking for more is a 400, not a larger page.
 *
 * One page is the whole set today (4 brands) and comfortably will be for a
 * while. When it is not, this becomes a typeahead rather than a bigger fetch:
 * a select is the wrong control past a screenful either way.
 */
export async function listAdminBrands(): Promise<AdminBrandSummary[]> {
	// Through the seam AND `toParams`, like every other read. Brands carry
	// no scoping today, so this is a no-op twice over -- and that is the point:
	// the day the seam starts narrowing anything, a read that skipped it is a
	// read that quietly shows more than it should.
	const response = await apiClient.get("/api/v1/admin/catalog/brands", {
		params: toParams(scopeCatalogFilters({ perPage: MAX_PAGE_SIZE })),
	});
	return parseResponseData(
		response.data,
		adminBrandSummaryPageSchema,
		"GET /api/v1/admin/catalog/brands",
	).items;
}

/*
 * NO CATEGORY FILTER HERE YET, and its absence is deliberate.
 *
 * The taxonomy has 674 leaf categories. A flat `<select>` of 674 options is not
 * a filter, it is a scrolling exercise.
 *
 * It needs a searchable typeahead, and so does the product editor's category
 * picker. That is two consumers, which is when the component is worth building
 * properly -- the same reason this screen has no generic DataTable yet.
 */

export interface AdminCategorySummary {
	id: string;
	name: string;
	slug: string;
	isActive: boolean;
}

/**
 * Categories matching a search, for the typeahead.
 *
 * A search rather than a list because there are 674 of them, which is unusable
 * as a dropdown and wasteful to fetch on every focus.
 *
 * NOT because of a page cap: an earlier version of this comment claimed the
 * endpoint capped a page at 100 and would silently truncate. It does not --
 * `adminListCategoriesSchema` has no page parameters at all and the service is
 * deliberately unpaginated, so `perPage` was being silently stripped by zod and
 * the request returned all 674 rows every time. The control was right; the
 * reason given for it was not.
 */
export async function searchAdminCategories(
	q: string,
): Promise<AdminCategorySummary[]> {
	const response = await apiClient.get("/api/v1/admin/catalog/categories", {
		params: toParams({ q }),
	});
	return parseResponseData(
		response.data,
		adminCategorySummaryListSchema,
		"GET /api/v1/admin/catalog/categories",
	).items;
}

/*
 * Re-exported, not redefined. Both shapes live in `@shared/types`, which the
 * previous PR created for exactly this reason -- and this file forked them
 * anyway, with `unit`/`enumValues` already drifting to nullable in the copy.
 */
export type { PublishBlocker, SpecTemplateAttribute };

/**
 * One variant of a product, as the admin reads it.
 *
 * THE TWO TYPED AXES ARE NOT THE FREE-FORM BAG, and the split is the whole
 * reason this type spells both out. `variantAxis1Name/Value` and
 * `variantAxis2Name/Value` are what the schema calls "what the UI filters on";
 * `variantAttributes` is for values that are NOT browse axes. A declared axis
 * written into the bag produces a variant no browse filter can see, and a
 * template-impact count that reads only the bag reports zero for a category
 * whose variants carry the axis in the typed pair.
 *
 * The typed pair is also the cap: a category can have at most two browse axes,
 * because there are two columns.
 *
 * Retired variants never appear here. Every read filters `deleted_at is null`.
 */
export interface AdminVariant {
	id: string;
	productId: string;
	sku: string;
	brandSku: string | null;
	brandSkuKind: string | null;
	variantAxis1Name: string | null;
	variantAxis1Value: string | null;
	variantAxis2Name: string | null;
	variantAxis2Value: string | null;
	variantAttributes: Record<string, string | number> | null;
	images: CatalogProductImage[] | null;
	isActive: boolean;
}

export interface VariantInput {
	sku: string;
	brandSku?: string;
	variantAxis1Name?: string;
	variantAxis1Value?: string;
	variantAxis2Name?: string;
	variantAxis2Value?: string;
	variantAttributes?: Record<string, string | number>;
	/**
	 * A variant's own images. The column always existed; until U4 there was no
	 * control that sent it, which is why `product_variants.images` was empty
	 * everywhere and plan 008's M5 could not be run on a variant.
	 */
	images?: CatalogProductImage[];
	isActive?: boolean;
}

export async function createVariant(
	productId: string,
	input: VariantInput,
): Promise<AdminVariant> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/products/${encodeURIComponent(productId)}/variants`,
		input,
	);
	return parseResponseData(
		response.data,
		variantEnvelopeSchema,
		"POST /api/v1/admin/catalog/products/:id/variants",
	).variant;
}

export async function updateVariant(
	variantId: string,
	patch: Partial<VariantInput>,
): Promise<AdminVariant> {
	const response = await apiClient.patch(
		`/api/v1/admin/catalog/variants/${encodeURIComponent(variantId)}`,
		patch,
	);
	return parseResponseData(
		response.data,
		variantEnvelopeSchema,
		"PATCH /api/v1/admin/catalog/variants/:id",
	).variant;
}

/**
 * Retire a variant.
 *
 * A SOFT delete server-side, and irreversible from the admin: every variant read
 * filters `deleted_at is null`, so a retired variant is returned by no endpoint
 * and there is no restore path short of an engineer with a database connection.
 * The confirmation dialog is where that gets said.
 */
export async function retireVariant(variantId: string): Promise<void> {
	await apiClient.delete(
		`/api/v1/admin/catalog/variants/${encodeURIComponent(variantId)}`,
	);
}

export interface AdminProductDetail {
	/*
	 * `brandId` is REMOVED before being re-added, and that is not decoration.
	 * `CatalogProduct & { brandId?: string | null }` intersects `string |
	 * undefined` with `string | null | undefined` and collapses back to `string |
	 * undefined` -- so the null this endpoint really sends stayed invisible to the
	 * compiler while the code looked like it had been handled. `verification`
	 * needs no such treatment; `CatalogProduct` has no such field, so it is a
	 * clean addition rather than an override.
	 */
	product: Omit<CatalogProduct, "brandId"> & {
		brandId?: string | null;
		verification?: string | null;
	};
	brand: { id: string; name: string } | null;
	/*
	 * `hsnCode` widened in for the Commercial tab, which shows the category's
	 * code as classification context. The endpoint has always sent it; the type
	 * simply did not say so, which made it unreachable.
	 */
	category: {
		id: string;
		name: string;
		slug: string;
		hsnCode?: string | null;
	};
	variants: AdminVariant[];
	/*
	 * The SHARED type now, not a local redeclaration of a nested wire shape.
	 *
	 * This used to describe `{ template, attributes }` because that is what the
	 * endpoint sent, while `CatalogProduct.specTemplate` declared the flat shape
	 * -- so the contract had two private descriptions and neither client could
	 * use the shared one. The server flattens at the boundary now, so there is
	 * one shape and this adapter is gone.
	 */
	specTemplate: (SpecTemplate & { attributes: SpecTemplateAttribute[] }) | null;
	/*
	 * The projected prices. The admin handler calls the same `getProduct` as the
	 * public one, which attaches the headline `price` and every projected
	 * `prices` entry.
	 */
	price?: ProductPrice | null;
	prices?: ProductPrice[];
}

/**
 * One product, with the SPEC TEMPLATE of its category attached.
 *
 * The template is what the Specifications tab is generated from. Plan 008 calls
 * that the highest-value screen in the plan because a new category needs no code
 * change -- so nothing downstream may hard-code an attribute name (V2 greps for
 * exactly that).
 */
export async function getAdminProduct(id: string): Promise<AdminProductDetail> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/products/${encodeURIComponent(id)}`,
	);
	return parseResponseData(
		response.data,
		adminProductDetailSchema,
		"GET /api/v1/admin/catalog/products/:id",
	);
}

export interface GetAdminProductHistoryOptions {
	cursor?: string;
	limit?: number;
}

/**
 * One product's audit history, for the Provenance tab.
 *
 * `/history` on the catalogue router, not the global `/admin/logs`: the same
 * permission that opens the product opens its history, so a catalogue editor
 * does not need blanket admin-log access to see what they themselves changed.
 */
export async function getAdminProductHistory(
	id: string,
	options: GetAdminProductHistoryOptions = {},
): Promise<ProductHistoryPage> {
	const params = new URLSearchParams();
	if (options.cursor) params.set("cursor", options.cursor);
	if (options.limit !== undefined) params.set("limit", String(options.limit));
	const queryString = params.toString();
	const url = `/api/v1/admin/catalog/products/${encodeURIComponent(id)}/history${
		queryString ? `?${queryString}` : ""
	}`;

	const response = await apiClient.get(url);
	return parseResponseData(
		response.data,
		productHistorySchema,
		"GET /api/v1/admin/catalog/products/:id/history",
	);
}

// ============================================================================
// PRICES
// ============================================================================

/** The fourteen members of the server's `priceBasisEnum`. */
export type PriceBasis =
	| "ex_factory"
	| "ex_works_trade"
	| "list_price_ex_gst"
	| "distributor_net"
	| "dealer_net"
	| "mrp"
	| "mrp_incl_gst"
	| "mrp_basis_unstated"
	| "mrp_and_dealer_net"
	| "recommended_consumer"
	| "project_institutional"
	| "landed"
	| "street_indicative"
	| "avg_realisation";

export type PriceObservationStatus =
	| "published"
	| "on_request"
	| "not_published";

export type PriceVerification =
	| "unverified"
	| "auto_validated"
	| "human_reviewed"
	| "brand_confirmed";

/** One observation, as `GET /products/:id/price-history` sends it. */
export interface PriceHistoryEntry {
	id: string;
	variantId: string | null;
	basis: PriceBasis;
	basisLabel: string;
	/** Paise. Null for a price on request or not published. */
	amount: number | null;
	uom: string;
	city: string | null;
	state: string | null;
	status: PriceObservationStatus;
	effectiveFrom: string;
	effectiveTo: string | null;
	supersededById: string | null;
	verification: PriceVerification;
	confidence: number | null;
	source: string;
	sourceUrl: string | null;
	basisEvidence: string | null;
	/** True when the current-price projection cites this row. */
	isCited: boolean;
	notProjectableReason: string | null;
}

/** Every observation for a product, newest effective date first. */
export async function getPriceHistory(
	productId: string,
): Promise<PriceHistoryEntry[]> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/products/${encodeURIComponent(productId)}/price-history`,
	);
	return parseResponseData(
		response.data,
		priceHistorySchema,
		"GET /api/v1/admin/catalog/products/:id/price-history",
	).history;
}

/** What `POST /products/:id/price-observations` accepts. */
export interface RecordPriceInput {
	basis: PriceBasis;
	status: PriceObservationStatus;
	/** Integer paise. Present only when `status` is published. */
	amountPaise?: number;
	uom: string;
	/** Null records a price for the whole product. */
	variantId: string | null;
	/**
	 * `YYYY-MM-DD` for an earlier day, or an ISO instant for today. The server
	 * refuses a date more than a few minutes in the future.
	 */
	effectiveFrom: string;
	sourceUrl: string | null;
	basisEvidence: string | null;
	/** Absent means unverified. `human_reviewed` costs `admin:catalog:verify`. */
	verification?: "human_reviewed";
}

/** Why a recorded price is not the one customers see. */
export type PriceNotShownReason =
	| "no_amount"
	| "later_price_recorded"
	| "basis_not_shown"
	| "no_hsn_code"
	| "no_gst_rate"
	| "preferred_price_has_no_gst_rate"
	| "another_price_preferred";

/** What customers see for one item, as the projection holds it. */
export interface CustomerPrice {
	observationId: string;
	/** Paise, GST inclusive. */
	amountPaise: number;
	basis: PriceBasis;
	gstRateBp: number;
	/** Whether the source basis already included GST, so nothing was added. */
	isGstInclusive: boolean;
}

export interface RecordPriceResult {
	observationId: string;
	supersededIds: string[];
	/** Whether customers now see this price. */
	shown: boolean;
	/** Null exactly when `shown` is true. */
	notShownReason: PriceNotShownReason | null;
	/**
	 * What customers see for the item this price applies to after the save:
	 * this price, another one, or null when that item shows no price.
	 */
	customerPrice: CustomerPrice | null;
	/** The HSN code a GST rate is looked up for: the product's, else its category's. */
	hsnCode: string | null;
}

export async function recordPrice(
	productId: string,
	input: RecordPriceInput,
): Promise<RecordPriceResult> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/products/${encodeURIComponent(productId)}/price-observations`,
		input,
	);
	return parseResponseData(
		response.data,
		recordPriceResultSchema,
		"POST /api/v1/admin/catalog/products/:id/price-observations",
	);
}

/** Record who has checked an observation. Costs `admin:catalog:verify`. */
export async function verifyPriceObservation(
	observationId: string,
	verification: PriceVerification,
): Promise<void> {
	await apiClient.post(
		`/api/v1/admin/catalog/price-observations/${encodeURIComponent(observationId)}/verify`,
		{ verification },
	);
}

/** Thrown when the publish gate refuses, carrying EVERY reason it gave. */
export class PublishBlockedError extends Error {
	blockers: PublishBlocker[];

	constructor(blockers: PublishBlocker[]) {
		super("This product cannot be published yet");
		this.name = "PublishBlockedError";
		this.blockers = blockers;
	}
}

/**
 * Upload one catalogue image and return the URL to store.
 *
 * `POST /admin/catalog/products/:id/images`, NOT the generic `/api/v1/upload`.
 * That endpoint files everything as `users/{uploaderId}/...`, and the storage
 * proxy reads that prefix as single-owner-private -- so a product photograph
 * put there is readable by exactly one operator and 403 for every customer
 * browsing the catalogue. Local development cannot show it: with R2 disabled
 * the files come from `express.static` with no authorization at all, so it
 * looks perfect right up until R2 is switched on, by which point the images
 * are already stored under the wrong prefix.
 *
 * The admin route keys storage by PRODUCT and takes its gate from
 * `requirePermission(admin:catalog:write)` rather than from a query parameter
 * the browser picks.
 *
 * One file per call rather than a batch: the multiple-upload endpoint is
 * all-or-nothing, and an operator who chose ten photographs should not lose
 * nine because the tenth was the wrong format.
 */
export async function uploadImage(
	productId: string,
	file: File,
): Promise<string> {
	const body = new FormData();
	body.append("file", file);

	/*
	 * A TIMEOUT, because this request has no other way to end.
	 *
	 * The shared axios client sets none, which is survivable for JSON reads that
	 * fail fast; an upload is the slowest request the app makes, and a stalled
	 * one leaves the button disabled with no cancel and no error -- the only way
	 * out is a reload that discards every other unsaved edit in the editor.
	 *
	 * Two minutes rather than something tight: 10MB over a poor connection is
	 * legitimately slow, and a timeout that fires on a working upload is worse
	 * than none. Scoped to this call rather than the shared client, because
	 * changing that would alter every request in two apps.
	 */
	const response = await apiClient.post(
		`/api/v1/admin/catalog/products/${encodeURIComponent(productId)}/images`,
		body,
		{ timeout: 120_000 },
	);
	return parseResponseData(
		response.data,
		uploadedImageSchema,
		"POST /api/v1/admin/catalog/products/:id/images",
	).url;
}

/**
 * What `POST /admin/catalog/products` needs to make a row.
 *
 * Deliberately the REQUIRED set plus brand, and nothing else. `createProductSchema`
 * defaults or omits everything not here -- description, images, specifications,
 * slug, brandSku, status -- and a create form that asked for all of them would
 * be a worse copy of the editor, with the operator filling in specifications
 * before choosing the category that decides which specifications apply.
 */
export interface CreateProductInput {
	name: string;
	sku: string;
	categoryId: string;
	tier: string;
	uom: string;
	brandId?: string;
}

/**
 * Create a product as a DRAFT.
 *
 * Status is not a parameter. A new product has no specifications and no image,
 * so it cannot pass the publish gate; offering "create as active" would be
 * offering a button whose only outcome is a rejection. The editor publishes,
 * once there is something to publish.
 *
 * Returns the created row so the caller can navigate straight into it -- the
 * whole point of the screen is that creation is step one of a longer sitting,
 * not an act that returns you to a list to find what you just made.
 *
 * `CatalogProduct`, NOT `AdminProduct`. The create handler answers with the row
 * it inserted and joins nothing, so there is no `brandName` on it; declaring
 * the richer type here would be the same fixture-versus-wire lie that shipped a
 * price column reading a field the endpoint never sent (#290). The envelope is
 * `data.product`, not `data` -- unlike every read in this file, which is
 * precisely why it is worth writing down rather than pattern-matching.
 */
export async function createAdminProduct(
	input: CreateProductInput,
): Promise<CatalogProduct> {
	const response = await apiClient.post(
		"/api/v1/admin/catalog/products",
		input,
	);
	return parseResponseData(
		response.data,
		productEnvelopeSchema,
		"POST /api/v1/admin/catalog/products",
	).product;
}

/**
 * What duplicating a product accepts.
 *
 * Three fields, and the omissions are the design. `duplicateProduct` copies the
 * source row by OMISSION -- it destructures out the identity, the status, the
 * verification and the provenance, and carries everything else -- so a duplicate
 * inherits specifications, tier, images and coverage figures, and cannot inherit
 * published state. There is no field here that could make it, and the route
 * schema carries none either.
 */
export interface DuplicateProductInput {
	sku: string;
	/**
	 * Optional. The server defaults to a truncated "Copy of <name>", and the
	 * dialog leaves the field empty so that default is what an operator gets by
	 * pressing Duplicate -- requiring a name here would mean the server's default
	 * is never exercised by the only caller it has.
	 */
	name?: string;
	brandSku?: string;
}

export async function duplicateAdminProduct(
	id: string,
	input: DuplicateProductInput,
): Promise<CatalogProduct> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/products/${encodeURIComponent(id)}/duplicate`,
		input,
	);
	return parseResponseData(
		response.data,
		productEnvelopeSchema,
		"POST /api/v1/admin/catalog/products/:id/duplicate",
	).product;
}

export interface UpdateProductInput {
	name?: string;
	description?: string;
	brandId?: string | null;
	categoryId?: string;
	sku?: string;
	tier?: string;
	uom?: string;
	status?: ProductStatus;
	specifications?: Record<string, string | number | boolean>;
	/**
	 * The product's own HSN, overriding whatever its category supplies.
	 *
	 * `null` unsets it and falls back to the category; `undefined` leaves it
	 * alone. The publish gate reads the product's code first and the category's
	 * only as a fallback, so this is the field that makes a product in one of the
	 * 368 categories with no HSN publishable at all.
	 */
	hsnCode?: string | null;
	/**
	 * The whole list, in order. `updateProductSchema` replaces rather than
	 * merges, and the first entry is the primary image -- position is the only
	 * thing a `jsonb string[]` can record (the catalogue image metadata plan
	 * promotes the column).
	 */
	images?: CatalogProductImage[];
	/**
	 * The product's own GST rate, in BASIS POINTS -- 18% is 1800.
	 *
	 * NEVER INHERITED from the category. The schema is explicit that "rates are
	 * per-SKU in reality, and plywood alone spans 12% and 18%", so a product with
	 * no rate has a genuine gap rather than a value borrowed from above.
	 *
	 * `null` unsets it; `undefined` leaves it alone.
	 */
	gstRateBp?: number | null;
}

/**
 * Save a product.
 *
 * The publish gate answers with EVERY blocker at once in `data.blockers`, which
 * is the whole reason it returns a list rather than throwing on the first
 * problem: an operator sent back four times for four fields is how a ten-minute
 * product becomes a forty-minute one. This unwraps that into a typed error so
 * the editor can highlight all of them together rather than printing one
 * sentence and hiding the rest.
 */
export async function updateAdminProduct(
	id: string,
	input: UpdateProductInput,
): Promise<void> {
	try {
		await apiClient.patch(
			`/api/v1/admin/catalog/products/${encodeURIComponent(id)}`,
			input,
		);
	} catch (error) {
		const blockers = (
			error as {
				response?: { data?: { data?: { blockers?: PublishBlocker[] } } };
			}
		)?.response?.data?.data?.blockers;
		if (blockers && blockers.length > 0) {
			throw new PublishBlockedError(blockers);
		}
		throw error;
	}
}

// ============================================================================
// BRANDS
// ============================================================================

/**
 * A brand row in the admin list.
 *
 * `mergedIntoId` is what distinguishes the two ways a brand can be inactive, and
 * the list has to show them differently: WITHDRAWN means someone decided its
 * products are off sale, SUPERSEDED means it was a duplicate that got merged
 * away. Rendering both as "inactive" would put a business decision and a
 * data-cleanup artefact under one word.
 */
export interface AdminBrandRow {
	id: string;
	name: string;
	isActive: boolean;
	mergedIntoId: string | null;
	productCount: number;
}

/** Everything the brand editor reads and writes. */
export interface AdminBrandDetail extends AdminBrandRow {
	description: string | null;
	website: string | null;
	logoUrl: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	// Plan 006 brand master (issue #298).
	segment: "micro" | "small" | "big" | "large" | null;
	positioning: "economy" | "mid" | "premium" | "luxury" | null;
	countryOrigin: string | null;
	isImported: boolean;
	parentBrandId: string | null;
	manufacturer: string | null;
	distribution: string[] | null;
	tierBasis: "revenue" | "listing" | "footprint" | "manual" | null;
	tierAsOfDate: string | null;
}

/** What one patch applied to many products did, and what it refused. */
export interface BulkUpdateResult {
	updated: number;
	/*
	 * NAMED. A refusal list is read by an operator deciding which products to go
	 * and fix, and a uuid does not answer that. It matters most at 500, where the
	 * refused rows are spread over twenty pages and none of them is on screen.
	 */
	skipped: Array<{ productId: string; name: string; reason: string }>;
}

/** The fields a bulk edit can set. Deliberately not name, SKU or slug. */
export interface BulkProductPatch {
	categoryId?: string;
	tier?: "standard" | "premium" | "luxury";
	status?: "draft" | "active" | "inactive";
	brandId?: string | null;
	verification?: string;
}

/**
 * One patch, many products (plan 008 V5).
 *
 * The response is not a bare success. A bulk publish over a real range will
 * contain products the gate refuses, and each comes back named with its reason
 * -- so the caller can tell the operator WHICH products still need work rather
 * than reporting a success that covers a partial result.
 */
export async function bulkUpdateProducts(
	productIds: string[],
	patch: BulkProductPatch,
	/**
	 * What the operator was shown before they confirmed.
	 *
	 * The server gets a list of ids and cannot reconstruct the filter that
	 * produced them -- it never leaves the browser. Sending the sentence is what
	 * lets the audit answer "what was this meant to cover" rather than only
	 * "which rows moved".
	 */
	intent?: string,
): Promise<BulkUpdateResult> {
	const response = await apiClient.post("/api/v1/admin/catalog/products/bulk", {
		productIds,
		patch,
		...(intent ? { intent } : {}),
	});
	return parseResponseData(
		response.data,
		bulkUpdateResultSchema,
		"POST /api/v1/admin/catalog/products/bulk",
	);
}

/**
 * The admin brand list. Includes withdrawn AND superseded brands, unlike the
 * public `listBrands` -- an operator needs to see both, and hiding a superseded
 * brand would make a merge look like a deletion.
 */
export async function listAdminBrandRows(filters: {
	q?: string;
	isActive?: boolean;
	page?: number;
}): Promise<AdminListPage<AdminBrandRow>> {
	const response = await apiClient.get("/api/v1/admin/catalog/brands", {
		params: toParams(
			scopeCatalogFilters({
				q: filters.q,
				isActive: filters.isActive,
				page: filters.page ?? 1,
				perPage: PER_PAGE,
			}),
		),
	});
	return parseResponseData(
		response.data,
		adminBrandRowPageSchema,
		"GET /api/v1/admin/catalog/brands",
	);
}

/** One brand, for the editor. */
export async function getAdminBrand(id: string): Promise<AdminBrandDetail> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(id)}`,
	);
	return parseResponseData(
		response.data,
		adminBrandDetailSchema,
		"GET /api/v1/admin/catalog/brands/:id",
	);
}

export type BrandInput = Partial<Omit<AdminBrandDetail, "id" | "productCount">>;

export async function createAdminBrand(
	input: BrandInput,
): Promise<{ id: string }> {
	const response = await apiClient.post("/api/v1/admin/catalog/brands", input);
	return parseResponseData(
		response.data,
		createdBrandSchema,
		"POST /api/v1/admin/catalog/brands",
	).brand;
}

export async function updateAdminBrand(
	id: string,
	input: BrandInput,
): Promise<void> {
	await apiClient.patch(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(id)}`,
		input,
	);
}

/**
 * One category a brand sells in, and what we hold there (issues #299, #255).
 *
 * `productCount` counts every status and `activeCount` only live ones, so a
 * category loaded and never published reads differently from one never loaded.
 */
export interface BrandCategoryCoverage {
	categoryId: string;
	slug: string;
	name: string;
	isPrimary: boolean;
	/** `brand-master` for a row the seed wrote, `manual` for an operator's. */
	source: "brand-master" | "manual";
	productCount: number;
	activeCount: number;
}

/** Every category a brand is assigned to, including the empty ones. */
export async function getBrandCategoryCoverage(
	brandId: string,
): Promise<BrandCategoryCoverage[]> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(brandId)}/category-coverage`,
	);
	return parseResponseData(
		response.data,
		brandCategoryCoverageListSchema,
		"GET /api/v1/admin/catalog/brands/:id/category-coverage",
	);
}

/**
 * Say a brand sells in a category. Idempotent, and answers with the brand's
 * whole coverage so the caller can redraw without a second read.
 */
export async function assignBrandCategory(
	brandId: string,
	categoryId: string,
): Promise<BrandCategoryCoverage[]> {
	const response = await apiClient.put(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(brandId)}/categories`,
		{ categoryId },
	);
	return parseResponseData(
		response.data,
		brandCategoryCoverageListSchema,
		"PUT /api/v1/admin/catalog/brands/:id/categories",
	);
}

export async function removeBrandCategory(
	brandId: string,
	categoryId: string,
): Promise<void> {
	await apiClient.delete(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(brandId)}/categories/${encodeURIComponent(categoryId)}`,
	);
}

/** A brand whose name is nearly the one being typed (issue #300). */
export interface BrandNearMatch {
	id: string;
	name: string;
	productCount: number;
	/**
	 * `same-name`: equal once punctuation, spacing and trading suffixes are
	 * folded away. `same-house`: the first word matches.
	 */
	reason: "same-name" | "same-house";
}

/**
 * Brands near `name`. Advisory: it refuses nothing, because four unrelated
 * companies are called `Anchor` and only a person can tell them apart.
 */
export async function findBrandNearMatches(
	name: string,
	excludeBrandId?: string,
): Promise<BrandNearMatch[]> {
	const response = await apiClient.get(
		"/api/v1/admin/catalog/brands/near-matches",
		// Through the seam: it lists brands, like the brand list does.
		{ params: toParams(scopeCatalogFilters({ name, excludeBrandId })) },
	);
	return parseResponseData(
		response.data,
		brandNearMatchListSchema,
		"GET /api/v1/admin/catalog/brands/near-matches",
	);
}

/** What a merge would do. See `previewBrandMerge` on the server. */
export interface MergePreview {
	loser: { id: string; name: string };
	winner: { id: string; name: string };
	movedProducts: number | null;
	collisions: string[];
	moreCollisions: boolean;
	blocker: string | null;
}

/**
 * The dry run behind the merge confirmation step (plan 008 M4).
 *
 * ADVISORY. It takes no locks, so a brand can be withdrawn or a colliding
 * product created between this call and the merge. The server re-checks under
 * lock, so a stale preview costs a clear refusal rather than a wrong merge --
 * which is why the dialog quotes it as "would" rather than "will".
 */
export async function previewBrandMerge(
	id: string,
	intoBrandId: string,
): Promise<MergePreview> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(id)}/merge-preview`,
		{ params: { intoBrandId } },
	);
	return parseResponseData(
		response.data,
		mergePreviewSchema,
		"GET /api/v1/admin/catalog/brands/:id/merge-preview",
	);
}

export async function mergeAdminBrands(
	id: string,
	intoBrandId: string,
): Promise<{ movedProducts: number }> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/brands/${encodeURIComponent(id)}/merge`,
		{ intoBrandId },
	);
	return parseResponseData(
		response.data,
		mergeResultSchema,
		"POST /api/v1/admin/catalog/brands/:id/merge",
	);
}

/*
 * CATEGORIES -- the taxonomy, not the picker.
 *
 * `searchAdminCategories` above answers "which category is this product in",
 * which is a typeahead over 674 rows. These answer "what does the taxonomy look
 * like and how do I change it", which needs the WHOLE tree at once: a node's
 * meaning is its position, and a page of it is not a tree.
 *
 * That is affordable for the same reason the endpoint is unpaginated -- roughly
 * 670 nodes, fetched once and nested on the client.
 */

/**
 * What the categories endpoint sends: the rows and the count, and no page
 * fields. It is unpaginated, so `page`, `perPage` and `totalPages` would be
 * `undefined` at runtime if the type promised them.
 */
export type AdminCategoryList = Pick<
	AdminListPage<AdminCategoryRow>,
	"items" | "total"
>;

/** A taxonomy node as the tree editor needs it. */
export interface AdminCategoryRow extends CatalogCategory {
	productCount: number;
	/*
	 * Whether this node carries a spec template, which decides whether it can
	 * take a child at all: templates live only on leaves. The server enforces it
	 * either way; this is what lets the tree say so before an operator types a
	 * name.
	 */
	hasTemplate: boolean;
}

/**
 * The whole taxonomy, flat, plus the count it was drawn from. The caller nests
 * it.
 *
 * `total` is returned rather than discarded because the endpoint CAPS at a
 * thousand rows. It reads as unpaginated -- there are no page parameters -- but
 * a taxonomy that outgrew the cap would come back truncated, and the tree
 * treats a row whose parent is missing as a root, so the screen would silently
 * re-parent whole subtrees to the top rather than fail. Comparing `items.length`
 * against `total` is the only way to tell.
 */
export async function listAdminCategories(): Promise<AdminCategoryList> {
	const response = await apiClient.get("/api/v1/admin/catalog/categories");
	return parseResponseData(
		response.data,
		adminCategoryRowListSchema,
		"GET /api/v1/admin/catalog/categories",
	);
}

export interface CreateCategoryInput {
	name: string;
	slug: string;
	description?: string;
	/** Absent creates a top-level category. */
	parentId?: string;
	displayOrder?: number;
}

export interface UpdateCategoryInput {
	name?: string;
	slug?: string;
	description?: string;
	displayOrder?: number;
	isActive?: boolean;
	/**
	 * Re-parent the category. `null` promotes it to the top level.
	 *
	 * The endpoint has always accepted this -- `updateCategorySchema` carries it
	 * and `updateCategory` guards it against cycles, the depth cap and stranded
	 * templates. Only this type omitted it, which is why re-parenting was
	 * unreachable from the admin and `CategoryDialog` says it does not offer it.
	 */
	parentId?: string | null;
}

export async function createAdminCategory(
	input: CreateCategoryInput,
): Promise<{ id: string }> {
	const response = await apiClient.post(
		"/api/v1/admin/catalog/categories",
		input,
	);
	return parseResponseData(
		response.data,
		createdCategorySchema,
		"POST /api/v1/admin/catalog/categories",
	).category;
}

export async function updateAdminCategory(
	id: string,
	input: UpdateCategoryInput,
): Promise<void> {
	await apiClient.patch(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(id)}`,
		input,
	);
}

/**
 * Remove a category's specification template.
 *
 * Not an operation anyone wants for its own sake -- it is step one of growing
 * the tree. Templates live only on leaves, so a category carrying one cannot be
 * given children until it is gone; the server refuses the child and says so.
 *
 * It costs something, and the caller has to be able to warn about it: until the
 * new leaves get templates of their own, the publish gate stops requiring those
 * attributes for products in this category. Stored specifications are untouched;
 * what lapses is the requirement.
 */
export async function deleteAdminCategoryTemplate(
	categoryId: string,
): Promise<{ deletedAttributes: number }> {
	const response = await apiClient.delete(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(categoryId)}/template`,
	);
	return parseResponseData(
		response.data,
		deletedTemplateSchema,
		"DELETE /api/v1/admin/catalog/categories/:id/template",
	);
}

/**
 * A category's specification template, flattened, or `null` if it has none.
 *
 * `null` is the ordinary state the editor opens in, not an error -- most of the
 * taxonomy carries no template, and creating the first one is the reason to open
 * this screen at all.
 *
 * NO UNWRAP, and the history is worth keeping because it happened TWICE.
 *
 * `getSpecTemplate` returns the internal pair `{ template, attributes }`. The
 * handler used to send that through as-is, so this function unwrapped it --
 * behind an `as` cast, which is why nothing type-checked the shape. Reading it
 * wrong the first time meant `.attributes` worked by coincidence (the wrapper
 * carries one too) while `.variantAttributes` came back `undefined`, so every
 * save from the editor wrote `[]` and wiped the category's variant axes. Four
 * reviewers found it independently.
 *
 * THEN THE SERVER WAS FLATTENED and this unwrap was left behind, which
 * reproduced the identical bug from the opposite direction: `wrapper.template`
 * became `undefined`, `{ ...undefined }` spread nothing, and the editor wiped
 * the axes again. Two reviewers found THAT independently. The commit that
 * flattened it said "both adapters are gone" and had counted two endpoints when
 * there were three.
 *
 * The cast is gone with the unwrap. `catalog-wire.db.test.ts` now pins this
 * endpoint's shape against a real database as well as `getProduct`'s -- pinning
 * one of the two flattened endpoints is exactly what let this through.
 */
export async function getAdminCategoryTemplate(
	categoryId: string,
): Promise<SpecTemplate | null> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(categoryId)}/template`,
	);
	return parseResponseData(
		response.data,
		templateEnvelopeSchema,
		"GET /api/v1/admin/catalog/categories/:id/template",
	).template;
}

/** One reason a move cannot happen, as the server words it. */
export interface CategoryMoveProblem {
	rule: "self" | "missing_parent" | "cycle" | "depth" | "template";
	message: string;
}

export interface CategoryMoveAssessment {
	problems: CategoryMoveProblem[];
	descendants: number;
	resultingDepth: number;
}

/**
 * What moving a category would cost, before doing it.
 *
 * The screen renders this and refuses on it. It must not restate any rule --
 * a second copy of the depth logic is exactly what the shared predicate exists
 * to prevent, and it would drift the first time the cap changed.
 *
 * `null` is a promotion to top level and is sent explicitly, because the server
 * distinguishes it from not asking.
 */
export async function previewCategoryMove(
	categoryId: string,
	parentId: string | null,
): Promise<CategoryMoveAssessment> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(categoryId)}/move/preview`,
		{ parentId },
	);
	return parseResponseData(
		response.data,
		categoryMoveAssessmentSchema,
		"POST /api/v1/admin/catalog/categories/:id/move/preview",
	);
}

/**
 * Renumber a whole sibling group.
 *
 * THE WHOLE GROUP, not a pair. `display_order` defaults to 0 and the taxonomy
 * sets it on 41 of 674 nodes, so almost every group is uniformly zero and really
 * name-ordered -- a two-id swap writes 0 over 0 and changes nothing.
 */
export async function reorderCategories(
	parentId: string | null,
	orderedIds: string[],
): Promise<{ updated: number }> {
	const response = await apiClient.post(
		"/api/v1/admin/catalog/categories/reorder",
		{ parentId, orderedIds },
	);
	return parseResponseData(
		response.data,
		reorderResultSchema,
		"POST /api/v1/admin/catalog/categories/reorder",
	);
}

export interface SpecTemplateInput {
	attributes: Array<{
		name: string;
		attributeType: string;
		isRequired: boolean;
		displayOrder: number;
		unit?: string;
		enumValues?: string[];
	}>;
	variantAttributes: Array<{
		name: string;
		type: string;
		enumValues?: string[];
	}>;
}

/**
 * What saving a template would do to the products already filed in the category.
 *
 * The counts an editing screen cannot show on its own: it displays a template,
 * and the damage lands on the catalogue underneath it.
 */
export interface SpecTemplateImpact {
	productCount: number;
	/** Stored values whose attribute name the proposal drops -- what a rename does. */
	orphaned: Array<{ name: string; products: number }>;
	/** Products that would newly fail the publish gate, and how many are live. */
	newlyRequired: Array<{ name: string; missing: number; active: number }>;
	retyped: Array<{ name: string; from: string; to: string; stored: number }>;
	/** Enum values products hold that the proposal would no longer allow. */
	narrowedEnums: Array<{ name: string; removed: string[]; products: number }>;
	/**
	 * Browse axes the proposal drops, and the live variants each would strand.
	 *
	 * Optional on the client because a server that predates this field answers
	 * without it, and a dialog that renders `impact.droppedAxes.length` on an
	 * older response would throw rather than show the other four counts.
	 */
	droppedAxes?: Array<{ name: string; variants: number }>;
}

/**
 * What saving this template would cost.
 *
 * `variantAttributes` is sent SEPARATELY from the attributes and is optional at
 * the wire: its absence means "not proposing anything about axes", which is what
 * an attribute-only save means, and is distinct from `[]` -- proposing none.
 * The server reads that difference, so a caller that always sent `[]` would ask
 * for a "you are dropping every axis" warning on every ordinary edit.
 */
export async function previewSpecTemplate(
	categoryId: string,
	attributes: Array<{
		name: string;
		attributeType: string;
		isRequired: boolean;
		enumValues?: string[];
	}>,
	variantAttributes?: Array<{
		name: string;
		type: string;
		enumValues?: string[];
	}>,
): Promise<SpecTemplateImpact> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(categoryId)}/template/preview`,
		{ attributes, ...(variantAttributes ? { variantAttributes } : {}) },
	);
	return parseResponseData(
		response.data,
		specTemplateImpactSchema,
		"POST /api/v1/admin/catalog/categories/:id/template/preview",
	);
}

export async function upsertAdminCategoryTemplate(
	categoryId: string,
	input: SpecTemplateInput,
): Promise<void> {
	await apiClient.post(
		`/api/v1/admin/catalog/categories/${encodeURIComponent(categoryId)}/template`,
		input,
	);
}

/**
 * The catalogue dashboard, in one request.
 *
 * No parameters, matching the endpoint. Every filter this could take is one the
 * products list applies better, and a dashboard that can be narrowed is one
 * whose numbers stop meaning "the catalogue".
 *
 * NOT wrapped in a try/catch that returns a default. The public reads in
 * apps/frontend do that because a failed product fetch there should render an
 * empty page rather than an error; here a zeroed dashboard is a LIE with the
 * same shape as a healthy one -- "nothing is blocked" is the answer an operator
 * most wants to see and the one they must not be shown by an outage.
 */
export async function getCatalogDashboard(): Promise<CatalogDashboard> {
	const response = await apiClient.get("/api/v1/admin/catalog/dashboard");
	return parseResponseData(
		response.data,
		catalogDashboardSchema,
		"GET /api/v1/admin/catalog/dashboard",
	);
}

/**
 * The cap the bulk endpoint enforces, restated here.
 *
 * `MAX_BULK_PRODUCTS = 500` lives in `catalog.service.ts` and is enforced twice
 * server-side, with a backend test importing it so the two cannot drift. The
 * admin client has no such link, so this is a second literal -- and it is one
 * for a SECOND REASON, which is why it is restated rather than treated as the
 * same fact.
 *
 * The server's cap bounds the work and lock duration of one transaction. This
 * one bounds how many paginated round trips the resolve below will make. They
 * are the same number today; raise the server's to a thousand and this should
 * be reconsidered rather than followed.
 */
export const MAX_BULK_SELECTION = 500;

/** The largest page the admin list endpoint will serve. */
const MAX_LIST_PAGE = 100;

export interface ResolvedSelection {
	/** Up to `MAX_BULK_SELECTION` ids, in the list's own order. */
	ids: string[];
	/** How many products match the filter, which may exceed `ids.length`. */
	total: number;
}

/**
 * Every product matching a filter, up to the bulk cap.
 *
 * WHY THIS EXISTS. The bulk endpoint takes ids, and the operator's intent is a
 * filter -- "all the drafts in Tiles". Ticking 500 rows across twenty pages is
 * not the same act, and a selection that survives paging is one the operator
 * can no longer see. Resolving the filter turns the intent into ids at one
 * instant, which is the only form the endpoint accepts.
 *
 * PAGINATED RATHER THAN A NEW ENDPOINT, deliberately. An ids-only route is the
 * obvious optimisation and should be earned by a measurement: five round trips
 * for 500 ids is not obviously slow, and a new endpoint is another surface to
 * gate, test and keep in step.
 *
 * It is only sound because `listProductsForAdmin` now breaks ties on `id`.
 * `updated_at` is not unique -- a bulk edit bumps thousands of rows to one
 * instant -- and without a tiebreak these statements could overlap and omit,
 * returning fewer ids than `total` claims, with no symptom.
 *
 * A SNAPSHOT, and that is correct. A product created after this returns is not
 * included; the operator acts on what they were shown. One changed in that
 * window is re-judged server-side by the publish gate.
 */
export async function resolveSelection(
	filters: ProductFilters,
): Promise<ResolvedSelection> {
	const ids: string[] = [];
	let total = 0;

	const pages = Math.ceil(MAX_BULK_SELECTION / MAX_LIST_PAGE);
	for (let page = 1; page <= pages; page += 1) {
		const result = await listAdminProducts({
			...filters,
			page,
			perPage: MAX_LIST_PAGE,
		});
		total = result.total;

		for (const item of result.items) {
			if (ids.length < MAX_BULK_SELECTION) ids.push(item.id);
		}

		// A short page is the last page. Without this, a filter matching twelve
		// products would still cost five round trips.
		if (result.items.length < MAX_LIST_PAGE) break;
		if (ids.length >= MAX_BULK_SELECTION) break;
	}

	/*
	 * The two must agree up to the cap. A disagreement means the pagination lost
	 * rows -- the tie hazard above, or a concurrent write -- and failing loudly
	 * beats acting on a set the operator was told was complete.
	 */
	const expected = Math.min(total, MAX_BULK_SELECTION);
	if (ids.length !== expected) {
		throw new Error(
			`Resolved ${ids.length} products for a filter matching ${total}. ` +
				"Refusing to act on an incomplete selection.",
		);
	}

	return { ids, total };
}
